/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { useAuth } from '../../contexts/AuthContext';
import { useEntityContext } from '../EntityContextSwitch';
import api from '../../services/api';
import PlatformAdminDialog from './PlatformAdminDialog';

const AVAILABLE_ROLES = [
  { id: 'admin', label: 'Entity Admin', color: 'error', description: 'Full access within this entity only' },
  { id: 'entity', label: 'Entity Manager', color: 'primary', description: 'Manage entity discovery and token associations' },
  { id: 'reports', label: 'Reports', color: 'success', description: 'Generate and export reports from templates' },
  { id: 'financial', label: 'Financial', color: 'warning', description: 'Access to financial data' },
  { id: 'leadership', label: 'Leadership', color: 'info', description: 'Access to leadership data' },
  { id: 'personal', label: 'Personal', color: 'secondary', description: 'Access to HR/personal data' },
  { id: 'basic', label: 'Basic', color: 'default', description: 'Read-only access' }
];

// Avatar component that uses our server-side photo proxy with caching
const UserAvatar = ({ user, size = 32 }) => {
  const { user: currentUser } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load photo for all users (not just Firebase users) since we have Workspace photos
    if (user.email && !imageError && currentUser && !loading) {
      const loadImage = async () => {
        setLoading(true);
        try {
          // Get auth token
          const token = await currentUser.getIdToken();

          // Fetch the image with authentication
          const photoUrl = api.getUserPhotoUrl(user.email);
          const response = await fetch(photoUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            mode: 'cors',
            credentials: 'omit',
            cache: 'force-cache'
          });

          if (response.ok) {
            // Convert response to blob and create object URL
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageSrc(objectUrl);
            setImageError(false);
          } else if (response.status === 404) {
            // No photo available, use initials
            setImageError(true);
          } else {
            throw new Error(`Failed to load photo: ${response.status}`);
          }
        } catch (error) {
          console.log(`Could not load photo for ${user.email}:`, error.message);
          setImageError(true);
          setImageSrc(null);
        } finally {
          setLoading(false);
        }
      };

      loadImage();
    }

    // Cleanup object URL on unmount
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.email, currentUser]); // Removed imageError from deps to prevent infinite loop

  const getInitials = (user) => {
    if (user.displayName) {
      const names = user.displayName.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[1][0]).toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    return user.email[0]?.toUpperCase() || '?';
  };

  // Show loading state
  if (loading) {
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          bgcolor: 'grey.300'
        }}
      >
        <CircularProgress size={size * 0.6} sx={{ color: 'grey.500' }} />
      </Avatar>
    );
  }

  // Use photo if available, otherwise show initials
  if (imageSrc && !imageError) {
    return (
      <Avatar
        src={imageSrc}
        sx={{
          width: size,
          height: size
        }}
      >
        {getInitials(user)}
      </Avatar>
    );
  }

  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: user.isPlatformAdmin ? 'error.main' : 'primary.main'
      }}
    >
      {getInitials(user)}
    </Avatar>
  );
};

// eslint-disable-next-line max-lines-per-function
function UserManagement() {
  const { user: currentUser, isPlatformAdmin, isWorkspaceAdmin } = useAuth();
  const { currentEntity } = useEntityContext();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dialogError, setDialogError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [accessDialogAction, setAccessDialogAction] = useState(null); // 'enable' or 'disable'
  const [platformAdminDialogOpen, setPlatformAdminDialogOpen] = useState(false);
  const [platformAdminAction, setPlatformAdminAction] = useState(null); // 'grant' or 'revoke'

  // Get admin status from AuthContext
  const isCurrentUserPlatformAdmin = isPlatformAdmin;
  const isCurrentUserWorkspaceAdmin = isWorkspaceAdmin;
  const adminStatusLoading = false; // No longer need loading state since it's in AuthContext

  // Debug logging
  console.log('UserManagement - Admin status from AuthContext:', {
    isPlatformAdmin,
    isWorkspaceAdmin,
    isCurrentUserPlatformAdmin,
    isCurrentUserWorkspaceAdmin,
    userEmail: currentUser?.email
  });

  // Fetch users when client changes
  useEffect(() => {
    if (currentEntity?.id) {
      fetchUsers();
    } else {
      setUsers([]);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntity]);

  const fetchUsers = async () => {
    if (!currentEntity?.id) {
      setError('Please select an entity first');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.getWorkspaceUsers(100, null, null, currentEntity.id);

      // Transform Workspace users and fetch their roles
      const transformedUsersPromises = response.users.map(async (user) => {
        let userRoles = [];
        let isPlatformAdmin;

        try {
          // Fetch user roles by email
          let rolesData = null;
          try {
            rolesData = await api.getEntityUserRolesByEmail(user.email, currentEntity.id);
          } catch (rolesFetchError) {
            console.log(`Could not fetch roles for ${user.email}:`, rolesFetchError.message);
            // Continue without roles data
          }

          // Get entity-specific roles
          const entityRoles = rolesData?.entityRoles || [];

          // Check if user has platform admin role
          isPlatformAdmin = rolesData?.isPlatformAdmin || false;

          // Set userRoles for backward compatibility
          if (isPlatformAdmin) {
            userRoles.push('admin');
          }
          userRoles.push(...entityRoles);


          return {
            uid: user.id, // Using Google Workspace ID for now
            email: user.email || '',
            displayName: user.displayName || user.name || '',
            photoURL: user.thumbnailPhotoUrl || null,
            roles: userRoles,
            clientRoles: rolesData?.entityRoles || [],
            isPlatformAdmin: isPlatformAdmin,
            hasFirebaseAccount: rolesData?.hasFirebaseAccount || false,
            hasFgosAccess: rolesData?.hasFgosAccess || false,
            firebaseUid: rolesData?.firebaseUid || null,
            lastLogin: user.lastLoginTime || new Date().toISOString(),
            status: user.suspended ? 'suspended' : 'active',
            department: user.department || '',
            title: user.title || '',
            isAdmin: user.isAdmin || false
          };
        } catch (roleError) {
          console.error(`Error fetching roles for ${user.email}:`, roleError);
          return {
            uid: user.id,
            email: user.email || '',
            displayName: user.displayName || user.name || '',
            photoURL: user.thumbnailPhotoUrl || null,
            roles: [],
            clientRoles: [],
            isPlatformAdmin: false,
            hasFirebaseAccount: false,
            hasFgosAccess: false,
            firebaseUid: null,
            lastLogin: user.lastLoginTime || new Date().toISOString(),
            status: user.suspended ? 'suspended' : 'active',
            department: user.department || '',
            title: user.title || '',
            isAdmin: user.isAdmin || false
          };
        }
      });

      const transformedUsers = await Promise.all(transformedUsersPromises);
      setUsers(transformedUsers);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
      console.error('Error fetching workspace users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setSelectedRoles(user.clientRoles || []);
    setDialogError(null);
    setEditDialogOpen(true);
  };

  const handleToggleFgosAccess = async (user) => {
    // Check if current user is a workspace admin with User and Groups Admin role
    if (!adminStatusLoading && !isCurrentUserWorkspaceAdmin) {
      setError('Only Google Workspace administrators with "User and Groups Admin" role can manage FGOS access');
      return;
    }

    // Check if trying to modify own access
    if (user.email === currentUser?.email) {
      setError('You cannot modify your own FGOS access. Please ask another administrator.');
      return;
    }

    setSelectedUser(user);
    setAccessDialogAction(user.hasFgosAccess ? 'disable' : 'enable');
    setAccessDialogOpen(true);
  };


  const handleTogglePlatformAdmin = (user) => {
    // Check if current user is a workspace admin
    if (!adminStatusLoading && !isCurrentUserWorkspaceAdmin) {
      setError('Only Google Workspace administrators with "User and Groups Admin" role can manage platform admin privileges');
      return;
    }

    // Check if trying to modify own access
    if (user.email === currentUser?.email) {
      setError('You cannot modify your own admin privileges. Please ask another administrator.');
      return;
    }

    setSelectedUser(user);
    setPlatformAdminAction(user.isPlatformAdmin ? 'revoke' : 'grant');
    setPlatformAdminDialogOpen(true);
  };

  const handleConfirmAccessChange = async () => {
    try {
      setLoading(true);
      setAccessDialogOpen(false);

      if (accessDialogAction === 'disable') {
        await api.disableFgosAccess(selectedUser.email);
        setSuccessMessage(`FGOS access disabled for ${selectedUser.email}`);
      } else {
        await api.enableFgosAccess(selectedUser.email);
        setSuccessMessage(`FGOS access enabled for ${selectedUser.email}`);
      }

      // Refresh the user list to get updated status
      await fetchUsers();
    } catch (err) {
      setError(`Failed to update FGOS access: ${err.message}`);
      console.error('Error updating FGOS access:', err);
    } finally {
      setLoading(false);
      setSelectedUser(null);
      setAccessDialogAction(null);
    }
  };


  const handlePlatformAdminSuccess = async () => {
    setSuccessMessage(
      platformAdminAction === 'grant'
        ? `Platform admin privileges granted to ${selectedUser.email}`
        : `Platform admin privileges revoked from ${selectedUser.email}`
    );
    setPlatformAdminDialogOpen(false);
    setSelectedUser(null);
    setPlatformAdminAction(null);

    // Refresh the user list to get updated status
    await fetchUsers();
  };

  const handleSaveRoles = async () => {
    console.log('handleSaveRoles called');
    console.log('Current entity:', currentEntity);
    console.log('Selected user:', selectedUser);
    console.log('Selected roles:', selectedRoles);

    try {
      // Show saving state
      setLoading(true);

      // Check if we have a client context
      if (!currentEntity?.id) {
        console.log('No entity selected, showing error');
        setDialogError('Please select an entity first');
        setLoading(false);
        return;
      }

      console.log('About to call api.updateEntityUserRoles');
      console.log('Parameters:', {
        entityId: currentEntity.id,
        userEmail: selectedUser.email,
        roles: selectedRoles
      });

      // Call API to update roles - use the new endpoint
      const result = await api.updateEntityUserRoles(currentEntity.id, selectedUser.email, selectedRoles);
      console.log('API call returned:', result);
      console.log('Response details:', {
        success: result?.success,
        message: result?.message,
        roles: result?.roles
      });

      // Only update local state if API call succeeded
      setUsers(users.map(u =>
        u.email === selectedUser.email
          ? { ...u, clientRoles: selectedRoles }
          : u
      ));

      setSuccessMessage(`Roles updated for ${selectedUser.email}`);
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error in handleSaveRoles:', err);
      // Extract error message from response if available
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update user roles';
      setDialogError(errorMessage);
      console.error('Error updating roles:', err);
      // Keep dialog open on error so user can see the message
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoles(prev =>
      prev.includes(roleId)
        ? prev.filter(r => r !== roleId)
        : [...prev, roleId]
    );
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      {/* Entity Selection Warning */}
      {!currentEntity && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Entity Selection Required
          </Typography>
          <Typography variant="body2">
            Please select an entity from the dropdown in the header to manage user roles for that specific entity.
          </Typography>
        </Alert>
      )}
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">User Management</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => console.log('Add user')}
          >
            Invite User
          </Button>
        </Box>
      </Box>
      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {/* User Management Permissions Info */}
      {console.log('UserManagement render - workspace admin state:', {
        isCurrentUserWorkspaceAdmin,
        currentEntity: currentEntity?.id,
        adminStatusLoading,
        shouldShowWarning: !adminStatusLoading && !isCurrentUserWorkspaceAdmin && currentEntity
      })}
      {!adminStatusLoading && !isCurrentUserWorkspaceAdmin && currentEntity && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Limited permissions:</strong> You do not have the Google Workspace "User and Groups Admin" role.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • You cannot enable/disable FGOS access for users
          </Typography>
          <Typography variant="body2">
            • You can only assign entity roles if you have entity admin permissions for this entity
          </Typography>
        </Alert>
      )}
      {/* Search */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search users..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>FGOS Status</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Google Workspace Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" sx={{ py: 2 }}>
                    Loading users...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" sx={{ py: 2 }}>
                    No users found. {searchTerm ? 'Try a different search term.' : ''}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              // eslint-disable-next-line max-lines-per-function
              (filteredUsers.map((user) => (
                <TableRow key={user.uid || user.email}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ mr: 2 }}>
                        <UserAvatar user={user} size={32} />
                      </Box>
                      {user.displayName || 'Unknown User'}
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {(() => {

                      if (!user.hasFirebaseAccount && !user.hasFgosAccess) {
                        return (
                          <Chip
                            label="No Access"
                            size="small"
                            color="default"
                            variant="outlined"
                            onClick={() => handleToggleFgosAccess(user)}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                          />
                        );
                      }

                      if (user.hasFgosAccess) {
                        return (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Active"
                            size="small"
                            color="success"
                            variant="outlined"
                            onClick={user.email !== currentUser?.email ? () => handleToggleFgosAccess(user) : undefined}
                            sx={user.email !== currentUser?.email ? { cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } } : {}}
                          />
                        );
                      }

                      return (
                        <Chip
                          label="No Access"
                          size="small"
                          color="default"
                          variant="outlined"
                          onClick={() => handleToggleFgosAccess(user)}
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {/* Show platform admin role */}
                      {user.isPlatformAdmin && (
                        <Chip
                          label="Platform Admin"
                          size="small"
                          color="error"
                          title="Platform administrator with full system access"
                        />
                      )}

                      {/* Show client-specific roles */}
                      {user.clientRoles && user.clientRoles.length > 0 ? (
                        user.clientRoles.map(roleId => {
                          const role = AVAILABLE_ROLES.find(r => r.id === roleId);
                          return (
                            <Chip
                              key={roleId}
                              label={role?.label || roleId}
                              size="small"
                              color={role?.color || 'default'}
                              title={role?.description || ''}
                            />
                          );
                        })
                      ) : (
                        !user.isPlatformAdmin && (
                          <Chip
                            label="No client roles"
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.status}
                      size="small"
                      color={user.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={
                      !currentEntity ? "Select an entity first" :
                      !user.hasFirebaseAccount && !user.hasFgosAccess ? "User needs FGOS access first" :
                      "Edit roles"
                    }>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                          disabled={!currentEntity || (!user.hasFirebaseAccount && !user.hasFgosAccess)}
                        >
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={
                      user.email === currentUser?.email ? "You cannot modify your own access" :
                      user.hasFgosAccess ? "Disable FGOS access" : "Enable FGOS access"
                    }>
                      <span>
                        <IconButton
                          size="small"
                          color={user.hasFgosAccess ? "error" : "success"}
                          disabled={user.email === currentUser?.email}
                          onClick={() => handleToggleFgosAccess(user)}
                        >
                          {user.hasFgosAccess ? (
                            <LockIcon />
                          ) : (
                            <AddCircleIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {!adminStatusLoading && isCurrentUserWorkspaceAdmin && (
                      <Tooltip title={
                        user.email === currentUser?.email ? "You cannot modify your own admin privileges" :
                        user.isPlatformAdmin ? "Revoke platform admin privileges" : "Grant platform admin privileges"
                      }>
                        <span>
                          <IconButton
                            size="small"
                            color={user.isPlatformAdmin ? "error" : "primary"}
                            disabled={user.email === currentUser?.email}
                            onClick={() => handleTogglePlatformAdmin(user)}
                          >
                            {user.isPlatformAdmin ? (
                              <RemoveCircleIcon />
                            ) : (
                              <AdminPanelSettingsIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              )))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Edit Roles Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Roles for {selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select the roles this user should have:
          </Typography>
          <FormGroup>
            {AVAILABLE_ROLES.map((role) => (
              <FormControlLabel
                key={role.id}
                control={
                  <Checkbox
                    checked={selectedRoles.includes(role.id)}
                    onChange={() => handleRoleToggle(role.id)}
                    disabled={role.id === 'admin' && selectedUser?.uid === currentUser?.uid}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">{role.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {role.description}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setDialogError(null);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRoles}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Roles'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* FGOS Access Dialog */}
      <Dialog
        open={accessDialogOpen}
        onClose={() => setAccessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {accessDialogAction === 'enable' ? 'Enable FGOS Access' : 'Disable FGOS Access'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {accessDialogAction === 'enable' ? (
              <>
                Allow <strong>{selectedUser?.displayName || selectedUser?.email}</strong> to log into the Faana Growth OS portal?
              </>
            ) : (
              <>
                Are you sure you want to disable FGOS access for <strong>{selectedUser?.displayName || selectedUser?.email}</strong>?
              </>
            )}
          </Typography>
          {accessDialogAction === 'enable' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This will grant the user access to sign into the FGOS portal. They will still need appropriate roles to access specific clients and features.
            </Alert>
          )}
          {accessDialogAction === 'disable' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will prevent the user from signing into the FGOS portal. Their role assignments will be preserved if you re-enable access later.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAccessDialogOpen(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={accessDialogAction === 'enable' ? 'primary' : 'error'}
            onClick={handleConfirmAccessChange}
            disabled={loading}
            startIcon={accessDialogAction === 'enable' ? <LockOpenIcon /> : <LockIcon />}
          >
            {loading ? 'Processing...' : accessDialogAction === 'enable' ? 'Enable Access' : 'Disable Access'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Platform Admin Dialog */}
      <PlatformAdminDialog
        open={platformAdminDialogOpen}
        onClose={() => {
          setPlatformAdminDialogOpen(false);
          setSelectedUser(null);
          setPlatformAdminAction(null);
        }}
        user={selectedUser}
        action={platformAdminAction}
        onSuccess={handlePlatformAdminSuccess}
      />
    </Box>
  );
}

export default UserManagement;